import React from "react";

import { toast } from "@performyard/py-components";

import { Divider } from "../../../../../components/Divider";
import Spacer from "../../../../../components/Spacer";

import { useAiSettings, useUpdateAiSettings } from "../../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlag";

import { SectionLayout, SettingsToggle } from "../../../Components";

const AIInsightsEmployeesSettingsTab = () => {
	const { value: coachingEnabled } = useFeatureFlagQuery("ff-is-coaching-enabled", false);

	// AI settings data management
	const { mutateAsync: updateAiSettings } = useUpdateAiSettings();
	const { data, isLoading } = useAiSettings();

	// toggles
	const isEmployeeInsightsEnabled = data?.employeeInsightsEnabled ?? true;
	const isEmployeeCoachingEnabled = data?.employeeCoachingEnabled ?? true;

	// toggle handlers
	const handleEmployeeInsightsToggle = async (val: boolean) => {
		await updateAiSettings({ employeeInsightsEnabled: val });
		toast.success(`Employee Insights ${val === true ? "Enabled" : "Disabled"}.`);
	};

	const handleEmployeeCoachingToggle = async (val: boolean) => {
		await updateAiSettings({ employeeCoachingEnabled: val });
		toast.success(`Employee Coaching Recommendations ${val === true ? "Enabled" : "Disabled"}.`);
	};

	if (isLoading) {
		return null;
	}

	return (
		<>
			<SectionLayout
				key="employee-insights-section"
				title="Employee Insights"
				description="The Employee Insights feature provides users with a continuously updated, comprehensive view of each employee's contributions, achievements, and overall performance."
				hideBottomSpacer
			>
				<SettingsToggle
					key="employee-insights-toggle"
					id="enable-employee-insights"
					label="Enable Employee Insights"
					description="Disabling this setting will remove PerformYard's ability to generate employee insights for every member of your organization."
					checked={isEmployeeInsightsEnabled}
					onCheckedChange={handleEmployeeInsightsToggle}
					dataTestId="employee-insights-toggle"
				/>
			</SectionLayout>

			{isEmployeeInsightsEnabled && coachingEnabled && (
				<>
					<Spacer key="employee-coaching-spacer-1" height={5} />
					<Divider key="employee-coaching-divider" />
					<Spacer key="employee-coaching-spacer-2" height={4} />

					<SectionLayout
						key="employee-coaching-section"
						title="Employee Coaching"
						description="Employee Coaching provides managers, administrators, and leaders throughout the chain of command with personalized coaching guidance based on employee insights."
					>
						<SettingsToggle
							key="employee-coaching-toggle"
							id="enable-employee-coaching-recommendations"
							label="Enable Employee Coaching Recommendations"
							description="Disabling this setting will hide the ability for managers, admins, and leaders to get coaching suggestions for their direct reports."
							checked={isEmployeeCoachingEnabled}
							onCheckedChange={handleEmployeeCoachingToggle}
							dataTestId="employee-coaching-toggle"
						/>
					</SectionLayout>
				</>
			)}
		</>
	);
};

export default AIInsightsEmployeesSettingsTab;
